import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import AbsenceTypeFiltersComponent from '@/features/absenceType/components/AbsenceTypeFilters';
import AbsenceTypeForm from '@/features/absenceType/components/AbsenceTypeForm';
import AbsenceTypeTable from '@/features/absenceType/components/AbsenceTypeTable';
import useAbsenceTypes from '@/features/absenceType/hooks/useAbsenceTypes';
import ParameterPagination from '@/features/parameter/components/ParameterPagination';
import Modal from '@/features/shared/components/Modal/Modal';
import { IoMdAdd } from 'react-icons/io';
const defaultFilters = {
    code: '',
    label: '',
    active: '',
};
const AbsenceTypesPage = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showAll, setShowAll] = useState(false);
    const [filters, setFilters] = useState({ ...defaultFilters });
    const [selectedAbsenceType, setSelectedAbsenceType] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { absenceTypes, meta, loading, error, handleCreateAbsenceType, handleUpdateAbsenceType, handleDeleteAbsenceType, refetchAbsenceTypes, } = useAbsenceTypes(currentPage, filters, showAll, rowsPerPage);
    const sortedAbsenceTypes = useMemo(() => {
        return [...absenceTypes].sort((a, b) => {
            const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
            if (orderDiff !== 0) {
                return orderDiff;
            }
            return a.label.localeCompare(b.label);
        });
    }, [absenceTypes]);
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setCurrentPage(1);
    };
    const handleEdit = (absenceType) => {
        setSelectedAbsenceType(absenceType);
        setIsModalOpen(true);
    };
    const handleDelete = async (absenceType) => {
        const confirmation = window.confirm(`Supprimer le type d'absence « ${absenceType.label} » ? Cette action est définitive.`);
        if (!confirmation) {
            return;
        }
        try {
            await handleDeleteAbsenceType(absenceType.id);
            toast.success("Type d'absence supprimé avec succès");
            refetchAbsenceTypes();
        }
        catch (apiError) {
            const message = apiError instanceof Error ? apiError.message : "Suppression impossible.";
            toast.error(message);
        }
    };
    const handleSubmit = async (payload) => {
        try {
            if (selectedAbsenceType) {
                await handleUpdateAbsenceType({ id: selectedAbsenceType.id, absenceType: payload });
                toast.success("Type d'absence mis à jour avec succès");
            }
            else {
                await handleCreateAbsenceType(payload);
                toast.success("Type d'absence créé avec succès");
            }
            setSelectedAbsenceType(null);
            setIsModalOpen(false);
            refetchAbsenceTypes();
        }
        catch (apiError) {
            const message = apiError instanceof Error ? apiError.message : 'Enregistrement impossible';
            toast.error(message);
            throw apiError;
        }
    };
    return (_jsxs("div", { className: "space-y-6", children: [_jsxs("header", { className: "flex flex-col gap-3 md:flex-row md:items-center md:justify-between", children: [_jsxs("div", { children: [_jsxs("div", { className: "mb-2 flex gap-4", children: [_jsx("h4", { className: "text-xl font-bold", children: "Types de cong\u00E9" }), _jsx("button", { onClick: () => setIsModalOpen(true), title: "Ajouter un type d'absence", className: "rounded", children: _jsx(IoMdAdd, { className: "bg-blue-500 text-white hover:bg-blue-600" }) })] }), _jsx("p", { className: "text-sm text-gray-600", children: "Configurez les types d'absence disponibles et leurs r\u00E8gles (validation, r\u00E9mun\u00E9ration, solde\u2026)." })] }), _jsx("div", { className: "flex items-center gap-4", children: _jsxs("label", { className: "inline-flex items-center gap-2 text-sm text-gray-700", children: [_jsx("input", { type: "checkbox", checked: showAll, onChange: event => setShowAll(event.target.checked) }), "Afficher tous les r\u00E9sultats (sans pagination)"] }) })] }), error && (_jsxs("div", { className: "rounded border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800", children: [_jsx("div", { className: "font-medium", children: "Une erreur est survenue" }), _jsx("p", { children: error }), _jsx("button", { type: "button", onClick: () => refetchAbsenceTypes(), className: "mt-3 rounded border border-yellow-400 px-3 py-1 text-yellow-700 hover:bg-yellow-100", children: "R\u00E9essayer" })] })), _jsx(AbsenceTypeFiltersComponent, { filters: filters, onFilterChange: handleFilterChange }), _jsxs("section", { className: "rounded border border-gray-200 bg-white shadow-sm", children: [_jsxs("header", { className: "flex items-center justify-between border-b border-gray-200 px-4 py-3", children: [_jsx("h2", { className: "text-lg font-medium text-gray-900", children: "Liste des types d'absence" }), _jsxs("span", { className: "text-sm text-gray-500", children: [sortedAbsenceTypes.length, " type(s)"] })] }), loading ? (_jsx("div", { className: "p-6 text-sm text-gray-500", children: "Chargement des types d'absence\u2026" })) : sortedAbsenceTypes.length === 0 ? (_jsx("div", { className: "p-6 text-sm text-gray-500", children: "Aucun type d'absence \u00E0 afficher avec les filtres actuels." })) : (_jsx(AbsenceTypeTable, { absenceTypes: sortedAbsenceTypes, onEdit: handleEdit, onDelete: handleDelete }))] }), !showAll && meta && meta.totalPages > 1 && (_jsx(ParameterPagination, { currentPage: meta.page, totalPages: meta.totalPages, limit: meta.limit, onPageChange: page => setCurrentPage(page), onLimitChange: value => {
                    setRowsPerPage(value);
                    setCurrentPage(1);
                } })), isModalOpen && (_jsx(Modal, { onClose: () => setIsModalOpen(false), children: _jsx(AbsenceTypeForm, { initialData: selectedAbsenceType ?? undefined, onSubmit: handleSubmit, onCancel: () => {
                        setIsModalOpen(false);
                        setSelectedAbsenceType(null);
                    } }) }))] }));
};
export default AbsenceTypesPage;
